import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { dateShort, money } from "../lib/format";
import { CalendarRange, Loader2, TrendingDown, TrendingUp } from "lucide-react";

const BUCKET_ORDER = [
  "Freight",
  "Fuel surcharge",
  "Accessorials / fines",
  "Adjustments",
  "Other",
  "Informational",
];

// Quick presets relative to a reference date (YYYY-MM-DD string from API)
function buildPresets(refDateStr) {
  const ref = refDateStr ? new Date(`${refDateStr}T00:00:00`) : new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  // Week containing ref (Sat→Fri to match UPS billing weeks)
  const back = (ref.getDay() + 1) % 7; // Sat=6 → back 0; Sun=0 → back 1; ...
  const thisWeekStart = new Date(ref);
  thisWeekStart.setDate(ref.getDate() - back);
  const thisWeekEnd = new Date(thisWeekStart);
  thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(thisWeekStart);
  lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
  const thisMonthStart = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const lastMonthStart = new Date(ref.getFullYear(), ref.getMonth() - 1, 1);
  const lastMonthEnd = new Date(ref.getFullYear(), ref.getMonth(), 0);
  return [
    { id: "this-week", label: "This week", from: fmt(thisWeekStart), to: fmt(thisWeekEnd) },
    { id: "last-week", label: "Last week", from: fmt(lastWeekStart), to: fmt(lastWeekEnd) },
    { id: "this-month", label: "This month", from: fmt(thisMonthStart), to: fmt(ref) },
    { id: "last-month", label: "Last month", from: fmt(lastMonthStart), to: fmt(lastMonthEnd) },
  ];
}

export default function PeriodSummary({ reportId, refreshKey, onRangeChange, onBucketClick }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("MTD");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customLoading, setCustomLoading] = useState(false);

  // Load base periods on report change
  useEffect(() => {
    api.periodSummary(reportId).then(setData).catch(() => setData(null));
  }, [reportId, refreshKey]);

  // When the user picks a custom range, refetch with it
  useEffect(() => {
    if (active !== "CUSTOM" || !customFrom || !customTo) return;
    setCustomLoading(true);
    api
      .periodSummary(reportId, customFrom, customTo)
      .then(setData)
      .catch(() => {})
      .finally(() => setCustomLoading(false));
  }, [active, customFrom, customTo, reportId, refreshKey]);

  // Notify parent whenever the effective range changes so the items table
  // can be filtered to match.
  useEffect(() => {
    if (!onRangeChange) return;
    if (active === "CUSTOM") {
      if (customFrom && customTo) {
        onRangeChange(customFrom, customTo, "CUSTOM");
      } else {
        onRangeChange(null, null, "CUSTOM");
      }
      return;
    }
    const p = data?.periods?.[active];
    if (p?.range_from && p?.range_to) {
      onRangeChange(p.range_from, p.range_to, active);
    }
  }, [active, customFrom, customTo, data, onRangeChange]);

  const presets = useMemo(
    () => buildPresets(data?.reference_date),
    [data?.reference_date],
  );

  if (!data) {
    return (
      <section className="bg-white border border-[#E5E5E5] rounded-md p-5 mb-8 text-xs text-[#525252] flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading period summary…
      </section>
    );
  }

  const periods = data.periods || {};
  const current =
    active === "CUSTOM" && (!customFrom || !customTo)
      ? {} // empty until a range is picked
      : periods[active] || {};
  const buckets = current.ups_by_bucket || {};
  const sortedBuckets = Object.entries(buckets).sort(
    (a, b) =>
      (BUCKET_ORDER.indexOf(a[0]) === -1 ? 999 : BUCKET_ORDER.indexOf(a[0])) -
      (BUCKET_ORDER.indexOf(b[0]) === -1 ? 999 : BUCKET_ORDER.indexOf(b[0])),
  );
  const totalUps = current.ups_billed || 0;
  const isLoss = (current.profit || 0) < 0;
  const showCustomPanel = active === "CUSTOM";

  return (
    <section
      className="bg-white border border-[#E5E5E5] rounded-md mb-8 overflow-hidden"
      data-testid="period-summary-panel"
    >
      <div className="px-5 pt-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Period summary</div>
          <div className="text-sm text-[#525252]">
            {current.range_from ? (
              <>
                {active === "CUSTOM" ? "Range" : "Range"}:{" "}
                <span className="font-mono-tabular text-[#0A0A0A]">
                  {dateShort(current.range_from)} → {dateShort(current.range_to)}
                </span>
              </>
            ) : (
              <>
                Reference date:{" "}
                <span className="font-mono-tabular text-[#0A0A0A]">
                  {dateShort(data.reference_date)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex border border-[#E5E5E5] rounded-md overflow-hidden">
          {["MTD", "QTD", "YTD", "CUSTOM"].map((p) => (
            <button
              key={p}
              onClick={() => setActive(p)}
              className={`px-4 py-1.5 text-xs font-medium transition inline-flex items-center gap-1 ${
                active === p
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-white text-[#525252] hover:bg-[#FAFAFA]"
              }`}
              data-testid={`period-tab-${p}`}
            >
              {p === "CUSTOM" ? (
                <>
                  <CalendarRange className="w-3 h-3" /> Custom
                </>
              ) : (
                p
              )}
            </button>
          ))}
        </div>
      </div>

      {showCustomPanel && (
        <div
          className="px-5 pt-4 border-t border-[#E5E5E5] mt-4 pb-4 bg-[#FAFAFA] grid grid-cols-1 md:grid-cols-[auto_auto_1fr] gap-3 items-end"
          data-testid="custom-range-controls"
        >
          <div>
            <label className="block eyebrow mb-1.5" htmlFor="custom-from">
              From
            </label>
            <input
              id="custom-from"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="border border-[#E5E5E5] bg-white rounded-md px-2.5 py-1.5 text-xs font-mono-tabular focus:border-[#0A0A0A] focus:outline-none"
              data-testid="custom-from-input"
            />
          </div>
          <div>
            <label className="block eyebrow mb-1.5" htmlFor="custom-to">
              To
            </label>
            <input
              id="custom-to"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="border border-[#E5E5E5] bg-white rounded-md px-2.5 py-1.5 text-xs font-mono-tabular focus:border-[#0A0A0A] focus:outline-none"
              data-testid="custom-to-input"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 md:justify-end">
            {presets.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setCustomFrom(p.from);
                  setCustomTo(p.to);
                }}
                className="px-2.5 py-1 text-[11px] border border-[#E5E5E5] bg-white hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] rounded-sm transition"
                data-testid={`preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showCustomPanel && (!customFrom || !customTo) ? (
        <div className="px-5 py-12 text-center text-sm text-[#525252]" data-testid="custom-empty">
          Pick a from and to date — or use a quick preset above — to view the
          summary.
        </div>
      ) : showCustomPanel && customLoading ? (
        <div className="px-5 py-12 text-center text-sm text-[#525252] flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Crunching range…
        </div>
      ) : (
        <>
          <div className="px-5 pt-5 grid grid-cols-1 md:grid-cols-3 gap-4 pb-5">
            <KpiBox
              label="Charged to customers (CMS)"
              value={money(current.cms_charged)}
              subtitle={`${current.cms_count || 0} shipments`}
              icon={<TrendingUp className="w-4 h-4" />}
              testId="period-cms"
            />
            <KpiBox
              label="UPS billed"
              value={money(current.ups_billed)}
              subtitle={`${current.ups_count || 0} line items`}
              icon={<TrendingDown className="w-4 h-4" />}
              testId="period-ups"
            />
            <KpiBox
              label="Profit / loss"
              value={
                current.profit != null
                  ? (current.profit >= 0 ? "" : "-") + money(Math.abs(current.profit))
                  : "—"
              }
              subtitle={isLoss ? "Loss this period" : "Net positive"}
              accent={isLoss ? "red" : "green"}
              testId="period-profit"
            />
          </div>

          <div className="border-t border-[#E5E5E5] px-5 py-4 bg-[#FAFAFA]">
            <div className="eyebrow mb-3">
              UPS spend breakdown ({active === "CUSTOM" ? "custom range" : active})
            </div>
            {sortedBuckets.length === 0 ? (
              <div className="text-xs text-[#525252]">No UPS data in this period.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedBuckets.map(([name, info]) => {
                  const pct = totalUps > 0 ? (info.amount / totalUps) * 100 : 0;
                  const color = bucketColor(name);
                  const clickable = !!onBucketClick;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => clickable && onBucketClick(name)}
                      className={`bg-white border border-[#E5E5E5] rounded-md p-3 text-left transition ${
                        clickable
                          ? "hover:border-[#0A0A0A] hover:shadow-sm cursor-pointer"
                          : "cursor-default"
                      }`}
                      data-testid={`bucket-${name.replace(/\s|\//g, "-")}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className="text-[11px] uppercase tracking-widest"
                          style={{ color }}
                        >
                          {name}
                        </span>
                        <span className="text-[11px] text-[#525252] font-mono-tabular">
                          {info.count} lines
                        </span>
                      </div>
                      <div className="font-mono-tabular text-lg font-medium">
                        {money(info.amount)}
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-[#F0F0EF] overflow-hidden">
                        <div
                          style={{
                            width: `${Math.min(100, pct)}%`,
                            background: color,
                          }}
                          className="h-full"
                        />
                      </div>
                      <div className="text-[11px] text-[#525252] mt-1 font-mono-tabular">
                        {pct.toFixed(1)}% of UPS spend
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function bucketColor(name) {
  switch (name) {
    case "Freight":
      return "#0A0A0A";
    case "Fuel surcharge":
      return "#CA8A04";
    case "Accessorials / fines":
      return "#DC2626";
    case "Adjustments":
      return "#7C3AED";
    case "Informational":
      return "#A3A3A3";
    default:
      return "#525252";
  }
}

function KpiBox({ label, value, subtitle, icon, accent, testId }) {
  const cls =
    accent === "red"
      ? "text-[#DC2626]"
      : accent === "green"
      ? "text-[#16A34A]"
      : "text-[#0A0A0A]";
  return (
    <div
      className="border border-[#E5E5E5] rounded-md p-4 bg-white"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        {icon && <span className="text-[#525252]">{icon}</span>}
      </div>
      <div className={`font-display text-2xl font-bold font-mono-tabular ${cls}`}>
        {value}
      </div>
      {subtitle && (
        <div className="text-[11px] text-[#525252] mt-1">{subtitle}</div>
      )}
    </div>
  );
}
